import { useState, useRef, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import neotexLogo from '../../assets/logos/product_logo.png'
import {
  setAgentData,
  chatSetMessages,
  chatSetQuestionStep,
  chatSetCollectedAnswers,
  chatSetPhotoPreview,
  chatSetProductsReady,
  chatSetProductsLoading,
  chatSetProductFlowStep,
  chatSetSelectedProduct,
  chatSetRoutineOpen,
  chatSetRoutineReady,
  chatSetRoutineData,
  chatSetAttemptCounts,
  chatSetValidationState,
  chatSetHairRetryCount,
  chatSetHairNeedsRetry,
  chatSetFlowType,
  chatReset,
  chatSetCompletedNewUserFlow,
} from '../../store'
import { CHAT_FLOWS, NEW_USER_FLOW } from './Questions'
import usePlan from '../../hooks/usePlan.js'
import '../../App.css'
import {
  NEW_USER_QUESTION_GATES,
  EXISTING_USER_QUESTION_GATES,
  BUTTON_STEP_IDS,
} from './config/chatConfig'
import {
  getTime,
  getMessageId,
  normalizePersistedId,
} from './utils/chatHelpers'
import { getAnswersMap, getFinalHairClass } from './utils/chatAnswerHelpers'
import {
  renderQuestionText,
  getTranslatedOptions as getTranslatedOptionsHelper,
  getWelcomeMessage,
} from './utils/chatTextHelpers'
import { formatProfileData } from './utils/profileSummaryHelpers'
import {
  getProductUrl,
  getProductRoutinePrompt,
  getProductDeclinePrompt,
  getLocalizedProductText,
} from './utils/productHelpers'
import { CHAT_MESSAGES } from './config/chatMessages'
import ChatHeader from './ChatHeader'
import ChatMessageList from './ChatMessageList'
import PictureQuestionPanel from './PictureQuestionPanel'
import ChatMessageBubble from './ChatMessageBubble'
import ChatActionPanel from './ChatActionPanel'
import ProductFlowActions from './ProductFlowActions'
import ChatQuestionInput from './ChatQuestionInput'
import usePhotoInput from '../../hooks/usePhotoInput'
import PhotoRetryAction from './PhotoRetryAction'
import ChatUpgradeAlert from './ChatUpgradeAlert'
import ChatDiagnosticsModal from './ChatDiagnosticsModal'
import useProductFlow from '../../hooks/useProductFlow'
import useChatInitialization from '../../hooks/useChatInitialization'
import useChatQuestionState from '../../hooks/useChatQuestionState'
import useChatMessages from '../../hooks/useChatMessages'
import useChatAnswers from '../../hooks/useChatAnswers'
import useChatAutoScroll from '../../hooks/useChatAutoScroll'
import useAgentRegistration from '../../hooks/useAgentRegistration'
import useChatTextInput from '../../hooks/useChatTextInput'
import useChatFlowHelpers from '../../hooks/useChatFlowHelpers'
import useChatSubmission from '../../hooks/useChatSubmission'
import usePhotoSubmission from '../../hooks/usePhotoSubmission'
import useHairPredictionRetry from '../../hooks/useHairPredictionRetry'
import useManualHairTypeSelection from '../../hooks/useManualHairTypeSelection'
import useChatCompletion from '../../hooks/useChatCompletion'
import useHairAnalysis from '../../hooks/useHairAnalysis'
import useChatValidation from '../../hooks/useChatValidation'
import useCameraStep from '../../hooks/useCameraStep'
import useSimpleStep from '../../hooks/useSimpleStep'
import useChatLocalization from '../../hooks/useChatLocalization'
import useQuestionNavigation from '../../hooks/useQuestionNavigation'
import useQuestionAdvance from '../../hooks/useQuestionAdvance'
import useRecommendationPayload from '../../hooks/useRecommendationPayload'
import useChatTranslations from '../../hooks/useChatTranslations'
import useChatOverlays from '../../hooks/useChatOverlays'
import ConsentModal from './ConsentModal.js'

const Chatbot = ({ goToPricing, goToAuthorisation }) => {
  const baseAgentData = useSelector((state) => state.agentData)
  const existingUserProfile = useSelector((state) => state.existingUserProfile)
  const existingUserData = existingUserProfile?.user_data
  const agentData = {
    ...baseAgentData,
    ...(existingUserData &&
    typeof existingUserData === 'object' &&
    !Array.isArray(existingUserData)
      ? existingUserData
      : {}),
  }
  const productRecommendations = useSelector(
    (state) => state.productRecommendations,
  )
  const store_id = useSelector((state) => state.store_id)
  const persistedAgentId = useSelector((state) => state.agent_id)
  const diagnosticsLLMText =
    useSelector((state) => state.diagnosticsLLMText) || ''
  const segmentedRawImage = useSelector(
  (state) => state.segmentedRawImage,)
  const language = useSelector((state) => state.language)
  const translationsData = useSelector((state) => state.translationsData) || {}
  const { translations: fetchedTranslations } = useChatTranslations({
    language,
  })
const translations =
  fetchedTranslations ||
  translationsData ||
  {}
  const dispatch = useDispatch()
  const bottomRef = useRef(null)
  const [typing, setTyping] = useState(false)
  const [photoFile, setPhotoFile] = useState(null)
  const [showCamera, setShowCamera] = useState(false)
  const [showManualHairTypeGrid, setShowManualHairTypeGrid] = useState(false)
  const [photoConsentOpen, setPhotoConsentOpen] = useState(false)
  const [photoConsentDetailView, setPhotoConsentDetailView] = useState(false)
  const [helpImproveConsentOpen, setHelpImproveConsentOpen] = useState(false)
  const [helpImproveConsentDetailOpen, setHelpImproveConsentDetailOpen] =
    useState(false)
  const [photoAnalysisConsent, setPhotoAnalysisConsent] = useState(null)
  const [modelTrainingConsent, setModelTrainingConsent] = useState(null)  
  const [recommendationDisclaimerOpen, setRecommendationDisclaimerOpen] =
    useState(false)
  const [recommendationDetailsOpen, setRecommendationDetailsOpen] =
    useState(false)
  const [
    recommendationDisclaimerDismissed,
    setRecommendationDisclaimerDismissed,
  ] = useState(false)
  const [photoConsentAcknowledgedStep, setPhotoConsentAcknowledgedStep] =
    useState(null)

  const selectedLanguage = String(language || 'EN')
    .trim()
    .toUpperCase()
  const photoConsentTranslations = translations?.chat?.photoConsent || {}
  const photoConsentDetailsTranslations =
    translations?.chat?.photoConsentDetails || {}
  const helpImproveConsentTranslations =
    translations?.chat?.helpImproveHalisi || {}
  const helpImproveConsentDetailTranslations =
    translations?.chat?.helpImproveHalisiDetails || {}
  const recommendationDisclaimerTranslations =
    translations?.chat?.recommendationDisclaimer || {}
  const recommendationAboutTranslations =
    translations?.chat?.recommendationAbout || {}

  const getLocalizedPhotoConsentText = (value, fallback = '') => {
    if (!value || typeof value !== 'object') return fallback
    return value[selectedLanguage] || value.EN || fallback
  }

  const handlePhotoConsentClose = () => {
    setPhotoConsentOpen(false)
    setPhotoConsentDetailView(false)
  }

  const handlePhotoConsentConfirm = (checkedState) => {
    setPhotoAnalysisConsent(
      !!checkedState?.photoConsent,
    )

    handlePhotoConsentClose()
    setHelpImproveConsentOpen(true)

    if (currentStepConfig?.id) {
      setPhotoConsentAcknowledgedStep(currentStepConfig.id)
    }
  }

  const handlePhotoConsentCancel = () => {
    setPhotoAnalysisConsent(false)

    handlePhotoConsentClose()

    if (currentStepConfig?.id) {
      setPhotoConsentAcknowledgedStep(currentStepConfig.id)
    }
  }

  const handleHelpImproveConsentConfirm = (checkedState) => {
    setModelTrainingConsent(
      !!checkedState?.helpImproveHalisi,
    )

    setHelpImproveConsentOpen(false)
    setHelpImproveConsentDetailOpen(false)

    if (currentStepConfig?.id) {
      setPhotoConsentAcknowledgedStep(currentStepConfig.id)
    }
  }

  const handleHelpImproveConsentReadMore = () => {
    setHelpImproveConsentOpen(false)
    setHelpImproveConsentDetailOpen(true)
  }

  const handleHelpImproveConsentClose = () => {
    setModelTrainingConsent(false)

    setHelpImproveConsentOpen(false)
    setHelpImproveConsentDetailOpen(false)

    if (currentStepConfig?.id) {
      setPhotoConsentAcknowledgedStep(currentStepConfig.id)
    }
  }

  const handleRecommendationDisclaimerConfirm = () => {
    setRecommendationDisclaimerOpen(false)
    setRecommendationDetailsOpen(false)
    setRecommendationDisclaimerDismissed(true)
  }

  const handleRecommendationDisclaimerClose = () => {
    setRecommendationDisclaimerOpen(false)
    setRecommendationDetailsOpen(false)
    setRecommendationDisclaimerDismissed(true)
  }

  const renderRecommendationAboutDetails = () => {
    const detailSections = [
      {
        heading: getLocalizedPhotoConsentText(
          recommendationAboutTranslations.whatAreTheseHeader,
          'About these recommendations',
        ),
        body: getLocalizedPhotoConsentText(
          recommendationAboutTranslations.whatAreThese,
          'These recommendations are generated from the hair profile and preferences you shared. They are designed to suggest products that may suit your hair and routine, but they are not guaranteed to be perfect for every person or every scalp condition.',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          recommendationAboutTranslations.howWeChooseHeader,
          'How we choose them',
        ),
        body: getLocalizedPhotoConsentText(
          recommendationAboutTranslations.howWeChoose,
          'We use the information you have entered about your hair type, goals, scalp concerns, routine, and any preferences you gave during the chat. We also consider the product features and ingredients available in our catalog.',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          recommendationAboutTranslations.notMedicalAdviceHeader,
          'Important note',
        ),
        body: getLocalizedPhotoConsentText(
          recommendationAboutTranslations.notMedicalAdvice,
          'Halisi gives beauty and cosmetic advice, not medical advice. If you have a scalp condition that worries you, please speak to a doctor or pharmacist.',
        ),
      },
    ]

    return (
      <div style={{ display: 'grid', gap: '1rem' }}>
        {detailSections.map(({ heading, body }) => (
          <div key={heading}>
            <strong style={{ display: 'block', marginBottom: '0.35rem' }}>
              {heading}
            </strong>
            <div style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
              {body}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderHelpImproveConsentDetails = () => {
    const detailSections = [
      {
        heading: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.whatWeWouldUseHeader,
          'What we would use',
        ),
        body: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.whatWeWouldUse,
          'The outline of your hair and the analysis result produced for you. We do not use your original photograph for this — it has already been deleted.',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.whatWeUseItForHeader,
          'What we use it for',
        ),
        body: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.whatWeUseItFor,
          'Training and testing the models that produce hair analyses, and checking that they work equally well across different hair types and textures. Nothing else. We do not use it for advertising, and we do not share it with brands.',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.whyAskSeparatelyHeader,
          'Why we ask separately',
        ),
        body: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.whyAskSeparately,
          'This is not needed to give you your hair analysis. It is a separate choice, and saying no has no effect on the service you receive.',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.howLongHeader,
          'How long we keep it',
        ),
        body: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.howLong,
          'For as long as the model generation it contributed to is in use, and for [X] months afterwards. Once data has been used to train a model, we cannot remove its influence from that model — but we will stop using your data in any future training as soon as you withdraw.',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.changingYourMindHeader,
          'Changing your mind',
        ),
        body: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.changingYourMind,
          'One tap in Settings, at any time. We will stop using your data for training and delete the copy we hold for that purpose.',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.fairnessHeader,
          'Fairness',
        ),
        body: getLocalizedPhotoConsentText(
          helpImproveConsentDetailTranslations.fairness,
          'We test our models across different hair types and textures, because an analysis that works well for some kinds of hair and badly for others is not a good product. Members who opt in help us do that better.',
        ),
      },
    ]

    return (
      <div style={{ display: 'grid', gap: '1rem' }}>
        {detailSections.map(({ heading, body }) => (
          <div key={heading}>
            <strong style={{ display: 'block', marginBottom: '0.35rem' }}>
              {heading}
            </strong>
            <div style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
              {body}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderPhotoConsentDetails = () => {
    const detailSections = [
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.whatWeProcessHeader,
          'What we process',
        ),
        items: photoConsentDetailsTranslations.whatWeProcess || [],
      },
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.whyPermissionHeader,
          'Why this needs your explicit permission?',
        ),
        body: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.whyPermission,
          'Your facial image ...',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.whatWeDoHeader,
          'What we do with it?',
        ),
        body: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.whatWeDo,
          'We use it only ...',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.howLongHeader,
          'How long we keep it?',
        ),
        items: photoConsentDetailsTranslations.howLong || [],
      },
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.whoSeesItHeader,
          'Who else sees it?',
        ),
        body: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.whoSeesIt,
          'Our cloud hosting ...',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.withdrawHeader,
          'Withdrawing your permission?',
        ),
        body: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.withdraw,
          'You can withdraw ...',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.ifYouWouldRatherNotHeader,
          'If you would rather not',
        ),
        body: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.ifYouWouldRatherNot,
          'You do not have to agree ...',
        ),
      },
      {
        heading: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.rightsHeader,
          'Your rights',
        ),
        body: getLocalizedPhotoConsentText(
          photoConsentDetailsTranslations.rights,
          'You can ask us ...',
        ),
      },
    ]

    return (
      <div className='halisi-modal-custom-content'>
        <p>
          <strong>
            {getLocalizedPhotoConsentText(
              photoConsentDetailsTranslations.section,
              'Who is responsible?',
            )}
          </strong>
        </p>
        <p>
          {getLocalizedPhotoConsentText(
            photoConsentDetailsTranslations.responsible,
            'Fit-For-Purpose Technologies SRL...',
          )}
        </p>

        {detailSections.map((section) => (
          <div key={section.heading}>
            <p>
              <strong>{section.heading}</strong>
            </p>
            {section.body && <p>{section.body}</p>}
            {section.items && (
              <ul>
                {(section.items || []).map((item, index) => (
                  <li key={`${section.heading}-${index}`}>
                    {getLocalizedPhotoConsentText(item, '')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    )
  }

  const {
    diagOpen,
    upgradeAlert,
    openDiagnostics,
    closeDiagnostics,
    openUpgradeAlert,
    closeUpgradeAlert,
    viewPlans,
  } = useChatOverlays({
    dispatch,
    setRoutineOpen: chatSetRoutineOpen,
    goToPricing,
  })

  const normalizedAgentDataId =
    normalizePersistedId(agentData?.agent_id) ||
    normalizePersistedId(agentData?.agentID)
  const effectiveAgentId =
    normalizedAgentDataId || normalizePersistedId(persistedAgentId)
  const agent_id = effectiveAgentId

  const chatFlowType = useSelector((state) => state.chatFlowType)
  const messages = useSelector((state) => state.chatMessages)

  const hasCompletedNewUserFlow = useSelector(
    (state) => state.chatCompletedNewUserFlow,
  )

  const { input, inputRef, handleInputChange, clearInput } = useChatTextInput()

  const { messagesRef, addAiMessage, addUserMessage } = useChatMessages({
    messages,
    dispatch,
    setMessages: chatSetMessages,
    getMessageId,
    getTime,
  })

  useChatAutoScroll({
    bottomRef,
    messages,
    typing,
  })

  const { getLocalizedText, getTranslatedOptions, getLocalizedWelcomeMessage } =
    useChatLocalization({
      translations,
      language,
      chatFlowType,
      effectiveAgentId,
      getTranslatedOptionsHelper,
    })

  useChatInitialization({
    chatFlowType,
    language,
    hasCompletedNewUserFlow,
    effectiveAgentId,
    agentFullName: agentData?.agentFullName,
    messages,
    dispatch,
    resetChat: chatReset,
    setFlowType: chatSetFlowType,
    setMessages: chatSetMessages,
    setQuestionStep: chatSetQuestionStep,
    translations,
    getWelcomeMessage,
    getLocalizedWelcomeMessage,
    getMessageId,
    getTime,
  })

  const questionStep = useSelector((state) => state.chatQuestionStep)
  const collectedAnswers = useSelector((state) => state.chatCollectedAnswers)
  const photoPreview = useSelector((state) => state.chatPhotoPreview)
  const productsReady = useSelector((state) => state.chatProductsReady)
  const productsLoading = useSelector((state) => state.chatProductsLoading)
  const productFlowStep = useSelector((state) => state.chatProductFlowStep)
  const selectedProduct = useSelector((state) => state.chatSelectedProduct)
  const productRoutineOpen = useSelector(
    (state) => state.chatProductRoutineOpen,
  )
  const productRoutineReady = useSelector(
    (state) => state.chatProductRoutineReady,
  )
  const productRoutineData = useSelector(
    (state) => state.chatProductRoutineData,
  )
  const attemptCounts = useSelector((state) => state.chatAttemptCounts)
  const validationState = useSelector((state) => state.chatValidationState)
  const hairPredictionRetryCount = useSelector(
    (state) => state.chatHairRetryCount,
  )
  const hairPredictionNeedsRetry = useSelector(
    (state) => state.chatHairNeedsRetry,
  )
  const environment = process.env.REACT_APP_ENV
  const is_trial = process.env.REACT_APP_TRIAL_STATUS
  const { canAccess } = usePlan()

  const { predictHairAttributes } = useHairAnalysis({
    environment,
    isTrial: is_trial,
    agentId: agent_id,
    dispatch,
  })

  const { onFileChange, handleCapture } = usePhotoInput({
    dispatch,
    setPhotoPreview: chatSetPhotoPreview,
    setPhotoFile,
    setShowCamera,
  })

  const fileRef = useRef(null)

  const {
    isExistingUser,
    questions: QUESTIONS,
    hairTypeStep,
    questionGates: QUESTION_GATES,
    lastInputStepIndex,
    currentStepConfig,
    translatedCurrentStepOptions,
    translatedHairTypeOptions,
    isPhotoStep,
    isButtonStep,
    isDone,
  } = useChatQuestionState({
    chatFlowType,
    effectiveAgentId,
    questionStep,
    getTranslatedOptions,
    newUserQuestionGates: NEW_USER_QUESTION_GATES,
    existingUserQuestionGates: EXISTING_USER_QUESTION_GATES,
    buttonStepIds: BUTTON_STEP_IDS,
    chatFlows: CHAT_FLOWS,
  })

  const {
    collectedAnswersRef,
    getCurrentAnswersMap,
    saveAnswer,
    upsertDerivedAnswer,
  } = useChatAnswers({
    collectedAnswers,
    questions: QUESTIONS,
    dispatch,
    setCollectedAnswers: chatSetCollectedAnswers,
  })

  const { buildRecommendationPayload } = useRecommendationPayload({
    getAnswersMap: getCurrentAnswersMap,
    collectedAnswersRef,
    agentData,
    storeId: store_id,
    agentId: agent_id,
    language,
  })

  const { shouldShowStep, isQuestionGated, findNextVisibleQuestion } =
    useChatFlowHelpers({
      questions: QUESTIONS,
      questionGates: QUESTION_GATES,
      canAccess,
      getAnswersMap: getCurrentAnswersMap,
    })

  const { requestRegistration } = useAgentRegistration({
    isExistingUser,
    agentData,
    environment,
    isTrial: is_trial,
    dispatch,
    setAgentData,
    getAnswersMap: getCurrentAnswersMap,
  })

  const renderStepText = (step, answersMap = getCurrentAnswersMap()) => {
    return renderQuestionText({
      step,
      localizedText: getLocalizedText(step.id),
      answersMap,
      agentData,
      translations,
      language,
    })
  }

  const {
    productRoutineLoading,
    fetchRecommendations,
    handleProductSelect,
    handleMetadataSubmission,
    handleViewProductRoutine,
    handleBuyDecision,
    handleBuyClick,
    handleContinueExploring,
    handleLogOut,
  } = useProductFlow({
    productRecommendations,
    selectedProduct,
    diagnosticsLLMText,
    productRoutineData,
    agentData,
    agentId: agent_id,
    buildRecommendationPayload,
    getAnswersMap: getCurrentAnswersMap,
    getMessageId,
    getTime,
    addAiMessage,
    addUserMessage,
    messagesRef,
    dispatch,
    setMessages: chatSetMessages,
    setProductsReady: chatSetProductsReady,
    setProductsLoading: chatSetProductsLoading,
    setProductFlowStep: chatSetProductFlowStep,
    setSelectedProduct: chatSetSelectedProduct,
    setRoutineOpen: chatSetRoutineOpen,
    setRoutineReady: chatSetRoutineReady,
    setRoutineData: chatSetRoutineData,
    resetChat: chatReset,
    goToAuthorisation,
    translations,
    language,
    consentData: {
      photoAnalysisConsent,
      modelTrainingConsent,
    },
    segmentedRawImage
  })

  const { completeQuestionFlow } = useChatCompletion({
    isExistingUser,
    questions: QUESTIONS,
    newUserQuestions: NEW_USER_FLOW,
    agentData,
    photoPreview,
    collectedAnswersRef,
    getAnswersMap: getCurrentAnswersMap,
    getFinalHairClass,
    formatProfileData: (answersMap) =>
      formatProfileData(
        answersMap,
        translations,
        language,
      ),
    renderStepText,
    addAiMessage,
    dispatch,
    setCompletedNewUserFlow: chatSetCompletedNewUserFlow,
    setQuestionStep: chatSetQuestionStep,
    fetchRecommendations,
  })

  const { goToStepById, askQuestion, goToNextStep } = useQuestionNavigation({
    questions: QUESTIONS,
    lastInputStepIndex,
    getAnswersMap: getCurrentAnswersMap,
    shouldShowStep,
    findNextVisibleQuestion,
    dispatch,
    setQuestionStep: chatSetQuestionStep,
    setTyping,
    renderStepText,
    getTranslatedOptions,
    addAiMessage,
    completeQuestionFlow,
  })

  const { handleManualHairTypeSelection } = useManualHairTypeSelection({
    hairTypeStep,
    questions: QUESTIONS,
    addUserMessage,
    saveAnswer,
    upsertDerivedAnswer,
    getAnswersMap: getCurrentAnswersMap,
    findNextVisibleQuestion,
    askQuestion,
    completeQuestionFlow,
    closeManualHairTypeGrid: () => setShowManualHairTypeGrid(false),
    closeCamera: () => setShowCamera(false),
  })

  const { handleValidatedStep } = useChatValidation({
    collectedAnswersRef,
    attemptCounts,
    validationState,
    dispatch,
    setValidationState: chatSetValidationState,
    setAttemptCounts: chatSetAttemptCounts,
    getAnswersMap: getCurrentAnswersMap,
    saveAnswer,
    addAiMessage,
    goToNextStep,
    translations,
    language,
  })

  const { handleCameraStep } = useCameraStep({
    predictHairAttributes,
    saveAnswer,
    upsertDerivedAnswer,
    dispatch,
    setPhotoPreview: chatSetPhotoPreview,
    setHairNeedsRetry: chatSetHairNeedsRetry,
    setHairRetryCount: chatSetHairRetryCount,
    hairPredictionRetryCount,
    addAiMessage,
    goToStepById,
    goToNextStep,
    translations,
    language,
  })

  const { retryHairPrediction, showHairPredictionRetryButton } =
    useHairPredictionRetry({
      hairPredictionRetryCount,
      hairPredictionNeedsRetry,
      dispatch,
      setHairRetryCount: chatSetHairRetryCount,
      setHairNeedsRetry: chatSetHairNeedsRetry,
      clearPhoto: () => {
        setPhotoFile(null)
        dispatch(chatSetPhotoPreview(null))
      },
      openCamera: () => {
        setShowCamera(true)
      },
    })

  const { handleSimpleStep } = useSimpleStep({
    saveAnswer,
    upsertDerivedAnswer,
    dispatch,
    setAgentData,
    agentData,
    getAnswersMap: getCurrentAnswersMap,
    isExistingUser,
    requestRegistration,
    goToNextStep,
  })

  const { advanceQuestion } = useQuestionAdvance({
    currentStepConfig,
    handleCameraStep,
    handleValidatedStep,
    handleSimpleStep,
  })

  useEffect(() => {
    const isPhotoQuestion = currentStepConfig?.id === 'hair_photo'

    if (isPhotoQuestion && photoConsentAcknowledgedStep !== 'hair_photo') {
      setPhotoConsentOpen(true)
      setPhotoConsentDetailView(false)
      return
    }

    if (!isPhotoQuestion) {
      setPhotoConsentOpen(false)
      setPhotoConsentDetailView(false)
    }
  }, [currentStepConfig?.id, photoConsentAcknowledgedStep])

  useEffect(() => {
    if (
      productsReady &&
      !recommendationDisclaimerDismissed &&
      !recommendationDisclaimerOpen &&
      !recommendationDetailsOpen
    ) {
      setRecommendationDisclaimerOpen(true)
    }
  }, [
    productsReady,
    recommendationDisclaimerDismissed,
    recommendationDisclaimerOpen,
    recommendationDetailsOpen,
  ])

  const { startConversation, send, handleButtonAnswer } = useChatSubmission({
    input,
    typing,
    productsLoading,
    productFlowStep,
    selectedProduct,
    questionStep,
    questions: QUESTIONS,
    getAnswersMap: getCurrentAnswersMap,
    findNextVisibleQuestion,
    askQuestion,
    advanceQuestion,
    addAiMessage,
    addUserMessage,
    clearInput,
    upsertDerivedAnswer,
    dispatch,
    setProductFlowStep: chatSetProductFlowStep,
    translations,
    language,
  })

  const { sendPhoto } = usePhotoSubmission({
    photoFile,
    photoPreview,
    typing,
    addUserMessage,
    advanceQuestion,
    setPhotoFile,
  })

  return (
    <>
      <ChatDiagnosticsModal
        diagOpen={diagOpen}
        productRoutineOpen={productRoutineOpen}
        productsReady={productsReady}
        productRoutineReady={productRoutineReady}
        productRoutineData={productRoutineData}
        selectedProduct={selectedProduct}
        diagnosticRequestData={buildRecommendationPayload()}
        onClose={closeDiagnostics}
      />
      <ChatUpgradeAlert
        visible={upgradeAlert.visible}
        minPlan={upgradeAlert.minPlan}
        onViewPlans={viewPlans}
        onClose={closeUpgradeAlert}
      />
      <div className='chat-root'>
        <ChatHeader agentData={agentData} />
        <ConsentModal
          isOpen={photoConsentOpen}
          size='lg'
          title={
            photoConsentDetailView
              ? getLocalizedPhotoConsentText(
                  photoConsentDetailsTranslations.title,
                  'How we use your photo and your hair analysis',
                )
              : getLocalizedPhotoConsentText(
                  photoConsentTranslations.title,
                  'Before we analyse your photo',
                )
          }
          message={
            photoConsentDetailView
              ? null
              : getLocalizedPhotoConsentText(
                  photoConsentTranslations.intro,
                  'To give you a hair analysis, we need to look at a photo of you. The photo shows your face, and the analysis tells us about the condition of your hair and scalp. Data protection law treats this as sensitive information, so we need your clear permission first.',
                )
          }
          subtext={
            photoConsentDetailView
              ? null
              : getLocalizedPhotoConsentText(
                  photoConsentTranslations.whatHappens,
                  'What happens to your photo:',
                )
          }
          items={[
              {
                id: 'photoConsent',
                label: getLocalizedPhotoConsentText(
                  photoConsentTranslations.checkboxLabel,
                  'I give Halisi my explicit permission to process my photo, and the information it reveals about my hair and scalp, in order to give me a hair analysis.',
                ),
                required: true,
                defaultChecked: photoAnalysisConsent === true,
              },
            ]}
          footnote={
            photoConsentDetailView ? null : (
              <>
                {getLocalizedPhotoConsentText(
                  photoConsentTranslations.footnote,
                  'You can withdraw this at any time in Settings.',
                ).replace(/\s*Read\s+the\s+full\s+details\.?\s*$/i, '')}{' '}
                <button
                  type='button'
                  onClick={() => setPhotoConsentDetailView(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#8c1f45',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {getLocalizedPhotoConsentText(
                    photoConsentTranslations.readMore,
                    'Read the full details.',
                  )}
                </button>
              </>
            )
          }
          confirmButtonText={getLocalizedPhotoConsentText(
            photoConsentTranslations.continue,
            'Continue',
          )}
          onCheckedChange={(state) => {
            setPhotoAnalysisConsent(
              !!state.photoConsent,
            )
          }}
          onConfirm={handlePhotoConsentConfirm}
          onCancel={handlePhotoConsentCancel}
        >
          {photoConsentDetailView ? (
            renderPhotoConsentDetails()
          ) : (
            <ul>
              {(photoConsentTranslations.bullets || []).map((bullet, index) => (
                <li key={`photo-bullet-${index}`}>
                  {getLocalizedPhotoConsentText(bullet, '')}
                </li>
              ))}
            </ul>
          )}
        </ConsentModal>

        <ConsentModal
          isOpen={helpImproveConsentOpen}
          size='lg'
          title={getLocalizedPhotoConsentText(
            helpImproveConsentTranslations.title,
            'Help improve Halisi (optional)',
          )}
          message={getLocalizedPhotoConsentText(
            helpImproveConsentTranslations.intro,
            'Our analysis gets better when it learns from a wider range of hair. If you are happy to help, we can keep your hair outline and analysis result to train and improve our models.',
          )}
          items={[
            {
              id: 'helpImproveHalisi',
              label: getLocalizedPhotoConsentText(
                helpImproveConsentTranslations.checkboxLabel,
                'I agree that Halisi may use my hair outline and analysis result to train and improve its hair analysis models.',
              ),
              required: false,
              defaultChecked: modelTrainingConsent === true,
            },
          ]}
          footnote={
            <>
              {getLocalizedPhotoConsentText(
                helpImproveConsentTranslations.footnote,
                'This is entirely optional. The app works exactly the same either way, and you can change your mind at any time in Settings.',
              )}{' '}
              <button
                type='button'
                onClick={handleHelpImproveConsentReadMore}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#8c1f45',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {getLocalizedPhotoConsentText(
                  helpImproveConsentTranslations.readMore,
                  'Read the full text.',
                )}
              </button>
            </>
          }
          confirmButtonText={getLocalizedPhotoConsentText(
            helpImproveConsentTranslations.continue,
            'Continue',
          )}
          onCheckedChange={(state) => {
            setModelTrainingConsent(
              !!state.helpImproveHalisi,
            )
          }}
          onConfirm={handleHelpImproveConsentConfirm}
          onCancel={handleHelpImproveConsentClose}
        />

        <ConsentModal
          isOpen={helpImproveConsentDetailOpen}
          size='lg'
          title={getLocalizedPhotoConsentText(
            helpImproveConsentDetailTranslations.title,
            'Helping us improve our hair analysis',
          )}
          confirmButtonText={getLocalizedPhotoConsentText(
            helpImproveConsentDetailTranslations.continue,
            'Continue',
          )}
          onConfirm={handleHelpImproveConsentClose}
          onCancel={handleHelpImproveConsentClose}
        >
          {renderHelpImproveConsentDetails()}
        </ConsentModal>

        <ConsentModal
          isOpen={recommendationDisclaimerOpen}
          size='lg'
          title={getLocalizedPhotoConsentText(
            recommendationDisclaimerTranslations.title,
            'Halisi gives beauty and cosmetic advice, not medical advice.',
          )}
          message={getLocalizedPhotoConsentText(
            recommendationDisclaimerTranslations.message,
            'If you have a scalp condition that worries you, please speak to a doctor or pharmacist.',
          )}
          footnote={
            <>
              <button
                type='button'
                onClick={() => setRecommendationDetailsOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#8c1f45',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {getLocalizedPhotoConsentText(
                  recommendationDisclaimerTranslations.readMore,
                  'Read full text.',
                )}
              </button>
            </>
          }
          confirmButtonText={getLocalizedPhotoConsentText(
            recommendationDisclaimerTranslations.continue,
            'Continue',
          )}
          onConfirm={handleRecommendationDisclaimerConfirm}
          onCancel={handleRecommendationDisclaimerClose}
        />

        <ConsentModal
          isOpen={recommendationDetailsOpen}
          size='lg'
          title={getLocalizedPhotoConsentText(
            recommendationAboutTranslations.title,
            'About these recommendations',
          )}
          confirmButtonText={getLocalizedPhotoConsentText(
            recommendationAboutTranslations.continue,
            'Continue',
          )}
          onConfirm={handleRecommendationDisclaimerClose}
          onCancel={handleRecommendationDisclaimerClose}
        >
          {renderRecommendationAboutDetails()}
        </ConsentModal>

        <ChatMessageList
          messages={messages}
          typing={typing}
          bottomRef={bottomRef}
          agentData={agentData}
          aiLogo={neotexLogo}
          selectedProduct={selectedProduct}
          onProductSelect={handleProductSelect}
          onOpenDiagnostics={openDiagnostics}
          goToPricing={goToPricing}
          loadingText={getLocalizedProductText({
            translations,
            language,
            keyPath: 'chatMessages.recommendationLoading',
            fallback: CHAT_MESSAGES.recommendationLoading,
          })}
          translations={translations}
          language={language}
        />
        <PhotoRetryAction
          visible={showHairPredictionRetryButton && !typing}
          disabled={typing}
          onRetry={retryHairPrediction}
        />
        <ProductFlowActions
          productFlowStep={productFlowStep}
          selectedProduct={selectedProduct}
          typing={typing}
          productRoutineLoading={productRoutineLoading}
          productUrl={getProductUrl(selectedProduct)}
          onViewRoutine={handleViewProductRoutine}
          onBuy={handleBuyClick}
          onDecline={() => handleBuyDecision('no')}
          onContinueExploring={handleContinueExploring}
          onLogOut={handleLogOut}
          translations={translations}
          language={language}
        />
        <ChatQuestionInput
          questionStep={questionStep}
          isPhotoStep={isPhotoStep}
          isButtonStep={isButtonStep}
          isDone={isDone}
          typing={typing}
          productsLoading={productsLoading}
          showHairPredictionRetryButton={showHairPredictionRetryButton}
          showCamera={showCamera}
          showManualHairTypeGrid={showManualHairTypeGrid}
          photoPreview={photoPreview}
          fileInputRef={fileRef}
          hairTypeOptions={translatedHairTypeOptions || []}
          currentStepOptions={translatedCurrentStepOptions || []}
          inputRef={inputRef}
          input={input}
          onInputChange={handleInputChange}
          onStartConversation={startConversation}
          onSend={send}
          onCapture={handleCapture}
          onUploadClick={() => fileRef.current?.click()}
          onTakePicture={() => setShowCamera(true)}
          onSelectHairType={() => {
            setShowManualHairTypeGrid(true)
            setShowCamera(false)
          }}
          onBackFromHairType={() => setShowManualHairTypeGrid(false)}
          onHairTypeSelect={handleManualHairTypeSelection}
          onRemovePhoto={(event) => {
            event.stopPropagation()
            setPhotoFile(null)
            dispatch(chatSetPhotoPreview(null))
          }}
          onSendPhoto={(event) => {
            event.stopPropagation()
            sendPhoto()
          }}
          onFileChange={onFileChange}
          onButtonAnswer={handleButtonAnswer}
          translations={translations}
          language={language}
        />
      </div>
    </>
  )
}

export default Chatbot
